import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  getPageKeyLabel,
  getSectionKeyLabel,
} from "@/modules/cms/domain/cms-labels";
import {
  isCmsHomeSectionKeyWithForm,
  isHiddenCmsSectionKey,
  type CmsContentAboutContactForm,
  type CmsContentAboutCoreValueForm,
  type CmsContentAboutHeroForm,
  type CmsContentAboutMissionForm,
  type CmsContentAboutServicesForm,
  type CmsContentAboutStoryForm,
  type CmsContentAboutGalleryForm,
  type CmsContentAboutPartnerForm,
  type CmsContentAboutTeamForm,
  type CmsContentAboutVisionForm,
  type CmsContentBannerForm,
  type CmsContentFooterForm,
  type CmsContentHomeContentForm,
  type CmsContentHomeHeroForm,
  type CmsContentPrivacyForm,
  type CmsContentTermsForm,
  type CmsSectionUpdateInput,
} from "@/modules/cms/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button } from "@devhop/ui";
import { useNavigate, useParams, useRouter } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useEffect } from "react";
import { useCmsSectionQuery, useUpdateCmsSection } from "../api/queries";
import { SectionForm } from "../ui/SectionForm";
import { AboutContactForm } from "../ui/forms/AboutContactForm";
import { AboutCoreValueForm } from "../ui/forms/AboutCoreValueForm";
import { AboutHeroForm } from "../ui/forms/AboutHeroForm";
import { AboutMissionForm } from "../ui/forms/AboutMissionForm";
import { AboutServicesForm } from "../ui/forms/AboutServicesForm";
import { AboutStoryForm } from "../ui/forms/AboutStoryForm";
import { AboutGalleryForm } from "../ui/forms/AboutGalleryForm";
import { AboutPartnerForm } from "../ui/forms/AboutPartnerForm";
import { AboutTeamForm } from "../ui/forms/AboutTeamForm";
import { AboutVisionForm } from "../ui/forms/AboutVisionForm";
import { BannerForm } from "../ui/forms/BannerForm";
import { FooterForm } from "../ui/forms/FooterForm";
import { HomeContentForm } from "../ui/forms/HomeContentForm";
import { HomeHeroForm } from "../ui/forms/HomeHeroForm";
import { PrivacyForm } from "../ui/forms/PrivacyForm";
import { TermsForm } from "../ui/forms/TermsForm";

export function CmsSectionEditPage() {
  const nav = useNavigate({ from: "/app/cms/sections/$id/edit" });
  const { id } = useParams({ from: "/app/cms/sections/$id/edit" });
  const { data: section, ...queryResult } = useCmsSectionQuery(id);
  const updateSection = useUpdateCmsSection(id, section?.pageKey ?? "home");
  const router = useRouter();

  const handleSubmit = async (values: CmsSectionUpdateInput) => {
    await updateSection.mutateAsync(values);
    router.history.back();
  };

  const handleTypedContentSubmit = async (
    content:
      | CmsContentHomeHeroForm
      | CmsContentHomeContentForm
      | CmsContentBannerForm
      | CmsContentFooterForm
      | CmsContentPrivacyForm
      | CmsContentTermsForm
      | CmsContentAboutHeroForm
      | CmsContentAboutStoryForm
      | CmsContentAboutVisionForm
      | CmsContentAboutMissionForm
      | CmsContentAboutCoreValueForm
      | CmsContentAboutTeamForm
      | CmsContentAboutServicesForm
      | CmsContentAboutPartnerForm
      | CmsContentAboutGalleryForm
      | CmsContentAboutContactForm,
  ) => {
    await updateSection.mutateAsync({ content });
    router.history.back();
  };

  const handleGalleryContentSubmit = async (
    content: CmsContentAboutGalleryForm,
  ) => {
    await updateSection.mutateAsync({ content });
  };

  const sectionKey = section?.sectionKey ?? "";

  useEffect(() => {
    if (sectionKey && isHiddenCmsSectionKey(sectionKey)) {
      nav({ to: "/app/cms/sections", search: { pageKey: section?.pageKey ?? "about" } });
    }
  }, [sectionKey, section?.pageKey, nav]);

  const useTypedForm = section && isCmsHomeSectionKeyWithForm(sectionKey);
  const sectionLabel =
    sectionKey && section?.pageKey
      ? `${getPageKeyLabel(section.pageKey)} / ${getSectionKeyLabel(sectionKey)}`
      : (id ?? "ແກ້ໄຂ");

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ສ່ວນຫນ້າເວັບ", to: "/app/cms/sections" },
            { label: sectionLabel },
          ]}
        />
      </Header>
      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ແກ້ໄຂ Section</h2>
            <p className="text-muted-foreground text-sm">
              {sectionKey && section?.pageKey && (
                <span>
                  {getPageKeyLabel(section.pageKey)} /{" "}
                  {getSectionKeyLabel(sectionKey)}
                </span>
              )}
            </p>
          </div>
          <Button variant="outline" onClick={() => router.history.back()}>
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <QueryState
          result={queryResult}
          title="ກຳລັງໂຫຼດ section"
          variant="fullscreen"
        >
          {!section
            ? null
            : useTypedForm && (
                <>
                  {sectionKey === "home.content" && (
                    <HomeContentForm
                      content={
                        (section.content ??
                          null) as CmsContentHomeContentForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "home.hero" && (
                    <HomeHeroForm
                      content={
                        (section.content ??
                          null) as CmsContentHomeHeroForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "global.banner" && (
                    <BannerForm
                      content={
                        (section.content ?? null) as CmsContentBannerForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "global.footer" && (
                    <FooterForm
                      content={
                        (section.content ?? null) as CmsContentFooterForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "global.privacy" && (
                    <PrivacyForm
                      content={
                        (section.content ??
                          null) as CmsContentPrivacyForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "global.terms" && (
                    <TermsForm
                      content={
                        (section.content ?? null) as CmsContentTermsForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "about.hero" && (
                    <AboutHeroForm
                      content={
                        (section.content ??
                          null) as CmsContentAboutHeroForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "about.story" && (
                    <AboutStoryForm
                      content={
                        (section.content ??
                          null) as CmsContentAboutStoryForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "about.vision" && (
                    <AboutVisionForm
                      content={
                        (section.content ??
                          null) as CmsContentAboutVisionForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "about.mission" && (
                    <AboutMissionForm
                      content={
                        (section.content ??
                          null) as CmsContentAboutMissionForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "about.core-value" && (
                    <AboutCoreValueForm
                      content={
                        (section.content ??
                          null) as CmsContentAboutCoreValueForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "about.team" && (
                    <AboutTeamForm
                      content={
                        (section.content ??
                          null) as CmsContentAboutTeamForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "about.services" && (
                    <AboutServicesForm
                      content={
                        (section.content ??
                          null) as CmsContentAboutServicesForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "about.partner" && (
                    <AboutPartnerForm
                      content={
                        (section.content ??
                          null) as CmsContentAboutPartnerForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "about.gallery" && (
                    <AboutGalleryForm
                      content={
                        (section.content ??
                          null) as CmsContentAboutGalleryForm | null
                      }
                      onSubmit={(c) => handleGalleryContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                  {sectionKey === "about.contact" && (
                    <AboutContactForm
                      content={
                        (section.content ??
                          null) as CmsContentAboutContactForm | null
                      }
                      onSubmit={(c) => handleTypedContentSubmit(c)}
                      onCancel={() => nav({ to: "/app/cms/sections" })}
                      submitting={updateSection.isPending}
                    />
                  )}
                </>
              )}
          {section && !useTypedForm && (
            <SectionForm
              mode="edit"
              pageKey={section.pageKey}
              section={section}
              onSubmit={handleSubmit}
              onCancel={() => nav({ to: "/app/cms/sections" })}
              submitting={updateSection.isPending}
            />
          )}
        </QueryState>
      </Main>
    </>
  );
}
